import { LogLevel } from '@environment/enums/log-level.enum';
import { captureException } from '@sentry/browser';
import { BUFFERED_REQUEST_STORAGE_PREFIX } from '@shared/extension/constants';
import { ExtendedRequestResponseEvent } from '@shared/http/interfaces';
import { postToBackend as httpPostToBackend } from '@shared/http/utils/post-to-backend';
import { captureSentryMessage } from '@shared/sentry/utils/sentry-events';
import { BLACKLISTED_DOMAINS, getLearningApp } from '@shared/utils';
import { runUntilException } from '@shared/utils/utils/functions';
import { ContentScriptStore } from '../store';
import { Blacklist } from '@shared/http/classes/blacklist';

export async function postToBackend(payload: ExtendedRequestResponseEvent): Promise<void> {
  try {
    if (isBlacklistedDomain(payload) || Blacklist.isRequestUrlBlacklisted(payload)) {
      return;
    }

    const store = ContentScriptStore.getInstance();
    payload.chromeProfileEmail ??= store.profileEmail$.value;
    const uniqueId = generateUniqueId();
    const key = generateStorageKey(BUFFERED_REQUEST_STORAGE_PREFIX, uniqueId);
    captureSentryMessage(
      `start postToBackend: ${key}, url: ${payload?.request?.url}, pageUrl: ${payload?.pageUrl}`,
      {},
      LogLevel.Debug,
      {
        url: payload?.request?.url,
        pageUrl: payload?.pageUrl,
        chromeId: chrome.runtime?.id,
      }
    );
    if (chrome.runtime?.id) {
      await chrome.storage.local.set({ [key]: payload });
    } else {
      if (!(await tryPostToBackend(store.backendUrl$.value, payload, key))) {
        runUntilException(saveToLocalStorage)(key, payload);
      }
    }
  } catch (e) {
    captureException(e);
  }
}

function isBlacklistedDomain(payload: ExtendedRequestResponseEvent): boolean {
  if (payload.request?.url === undefined) {
    return false;
  }

  const learningApp = getLearningApp(payload.pageUrl);
  const blacklistedDomains = BLACKLISTED_DOMAINS.get(learningApp) ?? [];
  if (blacklistedDomains.length === 0) {
    return false;
  }

  const hostname = new URL(payload.request.url).hostname;
  return blacklistedDomains.some(domain => hostname.match(domain));
}

function generateUniqueId(): string {
  return Math.random().toString(36).substring(2, 15);
}

function generateStorageKey(prefix: string, uniqueId: string): string {
  return `${prefix}::${uniqueId}::${Date.now()}`;
}

function saveToLocalStorage(key: string, payload: ExtendedRequestResponseEvent): void {
  payload.resent = true;
  console.log('Saving', key);
  localStorage.setItem(key, JSON.stringify(payload));
}

/**
 * Tries to post to backend and returns true if successful, false otherwise
 */
async function tryPostToBackend(backendUrl: string, payload: ExtendedRequestResponseEvent, requestKey?: string): Promise<boolean> {
  try {
    await httpPostToBackend(backendUrl, payload, requestKey);
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
}
